import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

export const VALID_FORMAT_MESSAGE = "Please provide 6 characters for a valid hex code";

export const COLOR_NONE = {
  label: "None",
  value: "none",
};

export const LINKS_TO_CHECK_BEFORE_REDIRECT = [
  "nav-link-clients",
  "router-link-logout",
  "sidebar-link-recent-creatives",
  "secondary-nav-link-profile",
  "sidebar-link-college-profile",
  "secondary-nav-link-approval-queue",
  "secondary-nav-link-campaigns",
];

const getRequiredHexInput = (inputName: string) =>
  z
    .string()
    .nonempty(`${inputName} color is required`)
    .regex(/^#[0-9A-Fa-f]{6}$/, VALID_FORMAT_MESSAGE);

const getOptionalHexInput = () =>
  z
    .string()
    .optional()
    .refine(
      (value) =>
        value === undefined ||
        value === "#" ||
        value.length === 0 ||
        value === "none" ||
        /^#[0-9a-fA-F]{6}$/.test(value),
      {
        message: VALID_FORMAT_MESSAGE,
      },
    );

export const ADOBE_FONTS = {
  name: "fonts.adobeFontsProjectId" as const,
  key: "adobeFontsProjectId" as const,
} as const;

export const GOOGLE_FONTS = {
  name: "fonts.googleFonts" as const,
  key: "googleFonts" as const,
} as const;

const optionSchema = z.object({
  label: z.string(),
  value: z.string(),
  icon: z.unknown().optional(),
  isAlertShown: z.boolean().optional(),
});

const paddingOptionSchema = z.object({
  left: z.string(),
  right: z.string(),
  top: z.string(),
  bottom: z.string(),
  isCustom: z.boolean(),
});

const layoutPaddingSchema = z.object({
  mobile: paddingOptionSchema,
  desktop: paddingOptionSchema,
});

// super refine to allow custom color to be conditionally required
const typographyFieldsValidation = z
  .object({
    fontFamily: optionSchema.optional(),
    fallbackFontFamily: optionSchema.optional(),
    fontSize: optionSchema.optional(),
    alignment: z.string().optional(),
    lineHeight: optionSchema.optional(),
    fontWeight: optionSchema.optional(),
    textColor: optionSchema.optional(),
    customColor: getOptionalHexInput(),
    linkColor: optionSchema.optional(),
    customLinkColor: getOptionalHexInput(),
  })
  .superRefine(
    (
      arg,
      ctx,
    ): arg is {
      fontFamily?: { label: string; value: string };
      fallbackFontFamily?: { label: string; value: string };
      fontSize?: { label: string; value: string };
      alignment?: string;
      lineHeight?: { label: string; value: string };
      fontWeight?: { label: string; value: string };
      textColor?: { label: string; value: string; icon?: unknown; isAlertShown?: boolean };
      customColor?: string;
      linkColor?: { label: string; value: string; icon?: unknown; isAlertShown?: boolean };
      customLinkColor?: string;
    } => {
      if (arg.textColor?.value === "custom") {
        if (!arg.customColor || !/^#[0-9a-fA-F]{6}$/.test(arg.customColor)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please provide 6 characters for a valid hex code",
            path: ["customColor"],
          });
        }
      }
      return false;
    },
  );

export const PREVIEW_FORM_DATA_VALIDATION_SCHEMA = zodResolver(
  z.object({
    colors: z.object({
      primary: getOptionalHexInput(),
      secondary: getOptionalHexInput(),
      tertiary: getOptionalHexInput(),
      intense: getRequiredHexInput("Intense"),
      bold: getRequiredHexInput("Bold"),
      soft: getRequiredHexInput("Soft"),
      subtle: getRequiredHexInput("Subtle"),
      white: getRequiredHexInput("White"),
    }),
    fonts: z.object({
      [GOOGLE_FONTS.key]: z.array(
        z
          .object({
            label: z.string().nullable(),
            value: z.string().nullable(),
            appliedToPreview: z.boolean().optional(),
          })
          .nullable()
          .optional(),
      ),
      [ADOBE_FONTS.key]: z
        .string()
        .optional()
        // Transform null/undefined to an empty string
        .transform((val) => val || ""),
    }),
    typography: z.object({
      heading: typographyFieldsValidation,
      subHeading: typographyFieldsValidation,
      paragraph: typographyFieldsValidation,
      buttonLabel: typographyFieldsValidation,
    }),
    button: z
      .object({
        padding: paddingOptionSchema,
        width: z.object({
          name: optionSchema,
          custom: z.string().optional(),
        }),
        alignment: z.string(),
        backgroundColor: z.object({
          name: optionSchema,
          custom: getOptionalHexInput(),
        }),
        borderRadius: z.object({
          name: optionSchema,
          custom: z.string().optional(),
        }),
        borderStyle: z.object({
          name: optionSchema,
          width: z.string().optional(),
          color: optionSchema.optional(),
          custom: getOptionalHexInput(),
        }),
      })
      .superRefine(
        (
          arg,
          ctx,
        ): arg is {
          padding: {
            left: string;
            right: string;
            top: string;
            bottom: string;
            isCustom: boolean;
          };
          width: {
            name: { label: string; value: string };
            custom?: string;
          };
          alignment: string;
          backgroundColor: {
            name: { label: string; value: string };
            custom?: string;
          };
          borderRadius: {
            name: { label: string; value: string };
            custom?: string;
          };
          borderStyle: {
            name: { label: string; value: string };
            width: string;
            color: { label: string; value: string };
            custom?: string;
          };
        } => {
          // super refine to allow custom color to be conditionally required and valid hex code
          if (arg.backgroundColor.name.value === "custom") {
            if (
              !arg.backgroundColor.custom ||
              !/^#[0-9a-fA-F]{6}$/.test(arg.backgroundColor.custom)
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Please provide 6 characters for a valid hex code",
                path: ["backgroundColor", "custom"],
              });
            }
          }
          // super refine to allow custom button conditionaly required
          // and in format 4px 4px 4px 4px with max value of 9999
          if (arg.borderRadius.name.value === "custom") {
            if (
              !arg.borderRadius.custom ||
              !/^\d{1,4}px \d{1,4}px \d{1,4}px \d{1,4}px$/.test(arg.borderRadius.custom) ||
              arg.borderRadius.custom.split(" ").some((value) => {
                const num = parseInt(value, 10);
                return num < 0 || num > 9999;
              })
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Please provide a valid border radius",
                path: ["borderRadius", "custom"],
              });
            }
          }
          // condtionally require custom color if borderColor is set to custom
          if (arg.borderStyle?.color?.value === "custom") {
            if (!arg.borderStyle.custom || !/^#[0-9a-fA-F]{6}$/.test(arg.borderStyle.custom)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Please provide 6 characters for a valid hex code",
                path: ["borderStyle", "custom"],
              });
            }
          }
          return false;
        },
      ),
    banner: z.object({
      imageUrl: z.string().optional(),
      fileName: z.string().optional(),
    }),
    layout: z.object({
      contentArea: paddingOptionSchema,
      contentBlock: z.object({
        group: optionSchema,
        allBlocks: layoutPaddingSchema,
        headings: layoutPaddingSchema,
        paragraph: layoutPaddingSchema,
        button: layoutPaddingSchema,
      }),
    }),
  }),
);
